// Leitura da resposta da IA — fonte única para conclusão, ACIA, relatório e tela.
//
// O modelo devolve um JSON com `conclusion_html` dentro, mas quase sempre embrulhado em
// ```json ... ``` e, com frequência, com quebras de linha literais dentro das strings —
// o que é JSON inválido. Quando o parse falhava, o código antigo salvava o texto cru
// inteiro como se fosse a conclusão, e o fisioterapeuta via `{ "conclusion_html": "` no
// começo do PDF e `"charts_data": [] } ```' no final.
//
// Regra da casa: o usuário nunca pode ver o envelope. Por isso são duas camadas —
// `parseAiJson` na escrita (extrai direito) e `unwrapAiHtml` na exibição (desfaz o
// envelope mesmo nos registros que já foram salvos errados, sem precisar de migração).

// Remove a cerca de código (```json ... ```) que o modelo adiciona por conta própria.
// A cerca nem sempre abre no início: às vezes vem depois de um "Claro! Aqui está:".
function stripCodeFence(text) {
    const raw = String(text == null ? '' : text).trim();

    const fenced = raw.match(/```[a-zA-Z]*\s*\n?([\s\S]*?)\n?\s*```/);
    if (fenced) return fenced[1].trim();

    // Cerca aberta sem fechamento (resposta truncada) — fica com o que veio depois dela.
    const unclosed = raw.match(/```[a-zA-Z]*\s*\n?([\s\S]*)$/);
    if (unclosed) return unclosed[1].trim();

    return raw;
}

// Do primeiro `{` até o último `}`: descarta qualquer preâmbulo do tipo
// "Claro! Aqui está o relatório:" que venha antes do JSON.
function extractJsonObject(text) {
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) return null;

    return text.slice(start, end + 1);
}

// Uma aspa dentro de uma string só encerra a string se o próximo caractere que importa
// for um dos que o JSON aceita depois de um valor. Se vier qualquer outra coisa, aquela
// aspa é conteúdo — HTML do tipo <td colspan="2"> ou uma citação no meio do texto.
function isStringTerminator(json, quoteIndex) {
    for (let index = quoteIndex + 1; index < json.length; index++) {
        const char = json[index];
        if (char === ' ' || char === '\n' || char === '\r' || char === '\t') continue;
        return char === ',' || char === '}' || char === ']' || char === ':';
    }

    // Aspa no fim do texto: só pode ser fechamento.
    return true;
}

// Escapa quebras de linha e caracteres de controle que ficaram soltos dentro das strings.
// É o defeito mais comum do JSON gerado por LLM e sozinho ele derruba o JSON.parse.
function repairControlChars(json) {
    let result = '';
    let insideString = false;
    let escaped = false;

    for (const char of json) {
        if (escaped) {
            result += char;
            escaped = false;
            continue;
        }

        if (char === '\\') {
            result += char;
            escaped = true;
            continue;
        }

        if (char === '"') {
            insideString = !insideString;
            result += char;
            continue;
        }

        if (insideString && char === '\n') { result += '\\n'; continue; }
        if (insideString && char === '\r') { result += '\\r'; continue; }
        if (insideString && char === '\t') { result += '\\t'; continue; }

        result += char;
    }

    return result;
}

// Reparo forte: além dos caracteres de controle, escapa as aspas que o modelo deixou
// soltas dentro das strings. É o defeito do Claude Sonnet no contrato da ACIA — ele
// escreve <td colspan="2"> dentro do conclusion_html sem escapar, e sem isso o JSON
// inteiro se perde: relatório cortado no meio e resumo/cruzamentos vazios.
function repairJson(json) {
    let result = '';
    let insideString = false;
    let escaped = false;

    for (let index = 0; index < json.length; index++) {
        const char = json[index];

        if (escaped) {
            result += char;
            escaped = false;
            continue;
        }

        if (char === '\\') {
            result += char;
            escaped = true;
            continue;
        }

        if (char === '"') {
            if (!insideString) {
                insideString = true;
                result += char;
                continue;
            }

            if (isStringTerminator(json, index)) {
                insideString = false;
                result += char;
                continue;
            }

            // Aspa de conteúdo: escapa e segue dentro da mesma string
            result += '\\"';
            continue;
        }

        if (insideString && char === '\n') { result += '\\n'; continue; }
        if (insideString && char === '\r') { result += '\\r'; continue; }
        if (insideString && char === '\t') { result += '\\t'; continue; }

        result += char;
    }

    return result;
}

// Devolve o objeto da resposta, ou null quando não há JSON aproveitável.
function parseAiJson(rawText) {
    const candidate = extractJsonObject(stripCodeFence(rawText));
    if (!candidate) return null;

    try {
        return JSON.parse(candidate);
    } catch {
        // Segue para o reparo.
    }

    try {
        return JSON.parse(repairControlChars(candidate));
    } catch {
        // Segue para o reparo forte.
    }

    try {
        return JSON.parse(repairJson(candidate));
    } catch {
        return null;
    }
}

// Último recurso: o JSON está quebrado demais para parsear, mas o HTML da conclusão está
// lá dentro. Recorta o valor de `conclusion_html` percorrendo a string até a aspa de
// fechamento não escapada. Melhor entregar o texto sem os cruzamentos do que despejar o
// envelope na cara do usuário.
function sliceQuotedValue(text, key) {
    const keyIndex = text.indexOf(`"${key}"`);
    if (keyIndex === -1) return null;

    const colonIndex = text.indexOf(':', keyIndex + key.length + 2);
    if (colonIndex === -1) return null;

    const openIndex = text.indexOf('"', colonIndex);
    if (openIndex === -1) return null;

    let escaped = false;
    for (let index = openIndex + 1; index < text.length; index++) {
        const char = text[index];

        if (escaped) { escaped = false; continue; }
        if (char === '\\') { escaped = true; continue; }
        // Aspa que não fecha o valor é conteúdo (atributo HTML, citação) — parar nela
        // era o que cortava o relatório no meio da tabela.
        if (char === '"' && isStringTerminator(text, index)) {
            return unescapeJsonString(text.slice(openIndex + 1, index));
        }
    }

    // String sem fechamento (resposta truncada): fica com tudo que veio.
    return unescapeJsonString(text.slice(openIndex + 1));
}

function unescapeJsonString(value) {
    return value
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\\//g, '/')
        .replace(/\\\\/g, '\\')
        .trim();
}

// Sinais de que o conteúdo ainda é o envelope da IA, e não o texto da conclusão.
function looksLikeAiEnvelope(text) {
    return /^```/.test(text)
        || (/^\s*\{/.test(text) && /"(conclusion_html|key_correlations|charts_data|executive_summary)"\s*:/.test(text));
}

// Rede de segurança da exibição: recebe o que está salvo em `text_content` e devolve
// sempre HTML limpo. Conteúdo já correto passa intacto.
function unwrapAiHtml(content) {
    const raw = String(content == null ? '' : content).trim();
    if (!raw) return '';

    const unfenced = stripCodeFence(raw);
    if (!looksLikeAiEnvelope(unfenced) && !looksLikeAiEnvelope(raw)) return unfenced;

    const parsed = parseAiJson(unfenced);
    if (parsed && typeof parsed.conclusion_html === 'string' && parsed.conclusion_html.trim()) {
        return parsed.conclusion_html.trim();
    }

    const sliced = sliceQuotedValue(unfenced, 'conclusion_html');
    if (sliced) return sliced;

    return unfenced;
}

// Campos que conclusão e ACIA leem da resposta. `conclusionHtml` nunca volta com o
// envelope: se o parse falhar de todo, vem o recorte do HTML e não o texto cru.
function extractAiPayload(rawText) {
    const parsed = parseAiJson(rawText) || {};
    const conclusionHtml = typeof parsed.conclusion_html === 'string' && parsed.conclusion_html.trim()
        ? parsed.conclusion_html.trim()
        : unwrapAiHtml(rawText);

    return {
        conclusionHtml,
        executiveSummary: typeof parsed.executive_summary === 'string' ? parsed.executive_summary.trim() : null,
        keyCorrelations: Array.isArray(parsed.key_correlations) ? parsed.key_correlations : [],
        documentsAnalyzed: Array.isArray(parsed.documents_analyzed) ? parsed.documents_analyzed : [],
        chartsData: Array.isArray(parsed.charts_data) ? parsed.charts_data : []
    };
}

module.exports = {
    parseAiJson,
    unwrapAiHtml,
    extractAiPayload
};
